import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Button, Container, Skeleton, styled, SxProps, Theme, useMediaQuery, useTheme, Select, MenuItem } from "@mui/material";
import { keyframes } from "@mui/system";
import { useEffect, useRef, useState } from "react";
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { TableLink } from "../../pages/EcoBank";
import { useAxios } from "../../services/http.service";
import { sizing } from "../../utils/methods";
import { ActionBtn, Text } from "../Typography";
import { useDragScroll } from "./useDragScroll";

// Skeleton loader → content reveal (transitions.dev "skeleton loader and reveal"). While a table's
// first page loads, the skeleton bars pulse; when the data lands, the skeleton layer fades out while
// the rows fade in on the same coordinates. Both sides share one duration and ease so the swap reads
// as a single motion (--duration-slow / --ease-in-out). The recipe's 2px cross-blur is left out on
// purpose: both surfaces span the whole table, and this project animates opacity/transform only.
const PULSE_MS = 1000;
const REVEAL_MS = 400;
const REVEAL_EASE = "ease-in-out";

const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const contentReveal = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export type TableSchema<T> = {
  id: string;
  label: string;
  render?: (data: T, i?: number) => any;
  sx?: SxProps<Theme>;
  headerStyle?: SxProps<Theme>;
  skeleton?: () => any;
};

type TableComponentProps = {
  schema: TableSchema<any>[];
  paginationDisabled?: boolean;
  link?: string;
  apiURL: string;
  title: string;
  mobileTitle: string;
  totalItems: number;
  batch?: number;
  hideTitle?: boolean;
  customData?: any[];
  errorName?: string;
  external?: boolean;
  removeAll?: boolean;
  searchFilter?: string;
  mainFilter?: string;
  isLoading?: boolean;
  // Rows in the first-load skeleton. Defaults to a full page, which is far too tall for a
  // customData table whose row count isn't known until it loads.
  skeletonRows?: number;
  equalColumnWidths?: boolean;
  filter?: {
    name: string;
    paramName?: string;
    omitOnAll?: boolean;
    values: string[] | Array<[any, string[]] | [any, string[], number]>;
  };
  dropdownFilter?: {
    name: string;
    paramName?: string;
    omitOnAll?: boolean;
    values: string[] | Array<[any, string[]] | [any, string[], number]>;
  };

  setLoading?(value: boolean): void;
};

const TableComponent = ({
  title,
  mobileTitle,
  schema,
  apiURL,
  totalItems,
  batch,
  setLoading,
  paginationDisabled,
  link,
  customData,
  filter,
  dropdownFilter,
  hideTitle,
  removeAll,
  searchFilter,
  errorName,
  isLoading,
  skeletonRows,
  equalColumnWidths,
  mainFilter,
}: TableComponentProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const axios = useAxios();

  const titleRef = useRef<HTMLDivElement | null>(null);
  // Click-and-drag panning for the horizontal overflow (desktop mice only — touch pans natively).
  const scrollRef = useDragScroll<HTMLDivElement>();
  const [data, setData] = useState<any[]>([]);
  const [loading, setIsLoading] = useState<boolean>(!!totalItems && !customData);
  const [isPaginating, setIsPaginating] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);

  // Use external loading state if provided, otherwise use internal state
  // Also consider paginating state to prevent "No data" flash during pagination
  const currentLoading = isLoading !== undefined ? isLoading : loading || isPaginating;

  // "loading" from the moment the first page (or a filter change) starts fetching until the reveal
  // after it has finished; "idle" otherwise. Pagination keeps its per-cell skeletons in place and
  // never comes through here.
  const showSkeleton = currentLoading && !isPaginating;
  const [revealPhase, setRevealPhase] = useState<"loading" | "idle">(showSkeleton ? "loading" : "idle");
  useEffect(() => {
    if (showSkeleton) {
      setRevealPhase("loading");
      return;
    }
    // The reveal itself is derived below, so there is no state change when it starts — the rows'
    // first render is the only render in that frame. This just ends it: unmount the skeleton layer
    // and drop the rows' reveal class once the cross-fade is done. (Already idle → React bails.)
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timer = window.setTimeout(() => setRevealPhase("idle"), reduceMotion ? 0 : REVEAL_MS);
    return () => window.clearTimeout(timer);
  }, [showSkeleton]);
  // A load that showed the skeleton and has just finished is the reveal.
  const skeletonMounted = showSkeleton || revealPhase === "loading";
  const contentRevealing = !showSkeleton && revealPhase === "loading";
  const revealClass = contentRevealing ? "is-revealing" : undefined;

  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const [currentFilter, setFilter] = useState<string>(filter ? "all" : "");
  const [currentDropdownFilter, setDropdownFilter] = useState<string>(dropdownFilter ? "all" : "");

  const limit = batch || 15;

  // const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // const open = Boolean(anchorEl);

  // const handleClick = (event: React.MouseEvent<HTMLElement>) => {
  //   setAnchorEl(event.currentTarget);
  // };

  // const handleClose = () => {
  //   setAnchorEl(null);
  // };

  const getFilter = (apiURL: string) => {
    let url = apiURL;
    let paramsAdded = false;
    const addParam = (key: string, val: string) => {
      const sym = paramsAdded || url.includes("?") ? "&" : "?";
      url += `${sym}${key}=${val}`;
      paramsAdded = true;
    };

    if (filter && currentFilter) {
      const values =
        typeof filter.values[0] === "string"
          ? filter.values
          : filter.values.map((item) => (item[1] as string[]).join(",")).join(",");
      const sort = filter.paramName ?? (url.includes("eco-bank") ? "orderBy" : filter.name);
      const skip = currentFilter === "all" && filter.omitOnAll;
      if (!skip) {
        addParam(sort, currentFilter === "all" ? (values as string) : currentFilter);
      }
    }

    if (dropdownFilter && currentDropdownFilter) {
      const values =
        typeof dropdownFilter.values[0] === "string"
          ? dropdownFilter.values
          : dropdownFilter.values.map((item) => (item[1] as string[]).join(",")).join(",");
      const sort = dropdownFilter.paramName ?? (url.includes("eco-bank") ? "orderBy" : dropdownFilter.name);
      const skip = currentDropdownFilter === "all" && dropdownFilter.omitOnAll;
      if (!skip) {
        addParam(sort, currentDropdownFilter === "all" ? (values as string) : currentDropdownFilter);
      }
    }

    return url + (paramsAdded || url.includes("?") ? "&" : "?");
  };

  const fetchData = async (page: number) => {
    const offset = (page - 1) * limit;
    setIsLoading(true);
    setError(null);
    // Don't clear existing data during pagination - let columns show skeletons instead
    try {
      // const lim = filter ? (currentFilter !== "all" ? 100 : limit) : limit;
      // const off = filter ? (currentFilter !== "all" ? 0 : offset) : offset;
      const lim = limit;
      const off = offset;
      const apiData = await axios.get(getFilter(apiURL) + `limit=${lim}&offset=${off}`).then((res) => res.data);
      setData(apiData);

      const isFilterActive = (currentFilter && currentFilter !== "all");
      const isDropdownActive = (currentDropdownFilter && currentDropdownFilter !== "all");

      if (isFilterActive || isDropdownActive) {
        const activeFilter = isFilterActive ? filter : dropdownFilter;

        if (activeFilter && activeFilter.values) {
          const filteredValues = activeFilter.values as Array<[string, string[], number]>;
          if (filteredValues && filteredValues[0] && typeof filteredValues[0] !== 'string' && filteredValues[0][2] !== undefined) {
            // Access the number (count) from the first tuple's third element
            const totalCount = filteredValues[0][2];
            const pagesCount = Math.ceil(totalCount / limit);
            const totalPageCount = pagesCount === 0 ? 1 : pagesCount;
            setTotalPages(totalPageCount);
          } else {
            const pagesCount = (Math.ceil(totalItems / limit));
            const totalPageCount = pagesCount === 0 ? 1 : pagesCount;
            setTotalPages(totalPageCount);
          }
        } else {
          const pagesCount = (Math.ceil(totalItems / limit));
          const totalPageCount = pagesCount === 0 ? 1 : pagesCount;
          setTotalPages(totalPageCount);
        }

      } else {
        const pagesCount = (Math.ceil(totalItems / limit));
        const totalPageCount = pagesCount === 0 ? 1 : pagesCount;
        setTotalPages(totalPageCount);
      }
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      setIsLoading(false);
      setIsPaginating(false); // Clear pagination loading state when fetch completes
      if (setLoading) {
        setLoading(false);
      }
    }
  };

  // Get paginated custom data
  const getPaginatedCustomData = () => {
    if (!customData) {
      return [];
    }
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return customData.slice(startIndex, endIndex);
  };

  useEffect(() => {
    if (totalItems && !customData) {
      fetchData(page);
    } else if (customData) {
      // Calculate total pages for custom data
      setTotalPages(Math.ceil(customData.length / limit));
      setIsLoading(false);
      if (setLoading) {
        setLoading(false);
      }
    }
  }, [page, totalItems, customData, limit]);

  useEffect(() => {
    if ((currentFilter && currentFilter === "all") || (currentDropdownFilter && currentDropdownFilter === "all")) {
      if (page !== 1) {
        setPage(1);
      } else {
        if (!customData) {
          fetchData(page);
        }
      }
    } else {
      if ((currentFilter || currentDropdownFilter) && !customData) {
        fetchData(page);
      }
    }
  }, [currentFilter, currentDropdownFilter, customData]);

  const handlePageClick = (pageNumber: number, scroll = false) => {
    // Set pagination loading immediately when page changes but keep existing data
    if (!customData && pageNumber !== page) {
      setIsPaginating(true);
      // Don't clear data - keep existing data visible with skeletons in columns
    }
    setPage(pageNumber);
    if (scroll) {
      setTimeout(() => titleRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  };

  // const getPageNumbers = () => {
  //   const pages = [];
  //   const startPage = Math.max(1, page === totalPages ? page - 2 : page - 1); // dynamically determine start
  //   const endPage = Math.min(totalPages, startPage + 2); // show 3 pages max

  //   for (let i = startPage; i <= endPage; i++) {
  //     pages.push(i);
  //   }

  //   return pages;
  // };

  useEffect(() => {
    if (searchFilter) {
      setFilter(searchFilter);
    }
  }, [searchFilter]);

  const getPreloaderSkeleton = () => {
    const totalItemsCount = customData ? customData.length : totalItems;
    const length = skeletonRows ?? (limit > totalItemsCount ? totalItemsCount || limit : limit);
    const arr = new Array(length).fill(0);
    const columnWidth = equalColumnWidths ? `${100 / schema.length}%` : "auto";

    return (
      <TableBody>
        {arr.map((_model, m_index) => (
          <TableRow key={m_index}>
            {schema.map((column, c_index) => (
              <Cell
                sx={{
                  height: { xs: 75, md: 148 },
                  pt: sizing(6),
                  pb: sizing(6),
                  paddingLeft: 0,
                  paddingRight: { xs: "18px", md: "24px" },
                  width: equalColumnWidths ? columnWidth : "auto",
                  ...column.sx,
                }}
                key={c_index}
              >
                {column.skeleton ? column.skeleton() : <Skeleton sx={{ ...column.sx }} />}
              </Cell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const getDataMapper = (data: any[]) => {
    const columnWidth = equalColumnWidths ? `${100 / schema.length}%` : "auto";

    return data.map((model, m_index) => {
      return (
        <TableRow key={m_index}>
          {schema.map((column, c_index) => (
            <Cell
              sx={{
                textTransform: "uppercase",
                pt: sizing(6),
                pb: sizing(6),
                paddingLeft: 0,
                paddingRight: { xs: "18px", md: "24px" },
                width: equalColumnWidths ? columnWidth : "auto",
                ...column.sx,
              }}
              key={c_index}
            >
              {isPaginating ? (
                // Show skeleton for all columns during pagination
                column.skeleton ? (
                  column.skeleton()
                ) : (
                  <Skeleton sx={{ ...column.sx }} />
                )
              ) : // Show normal data when not paginating
                column.render ? (
                  column.render(model, m_index)
                ) : (
                  model[column.id]
                )}
            </Cell>
          ))}
        </TableRow>
      );
    });
  };

  const renderDropdownFilter = () => {
    if (!dropdownFilter) return null;

    const extractText = (node: any): string => {
      if (node == null || typeof node === "boolean") return "";
      if (typeof node === "string" || typeof node === "number") return String(node);
      if (Array.isArray(node)) return node.map(extractText).join("");
      if (node.props && node.props.children) return extractText(node.props.children);
      return "";
    };

    let maxChar = (mainFilter || "All").length;
    dropdownFilter.values.forEach(v => {
      let str = "";
      if (typeof v === "string") str = v;
      else if (typeof v[0] === "string") str = v[0];
      else str = extractText(v[0]);
      if (str.length > maxChar) maxChar = str.length;
    });

    // Calculate width: text chars + MUI dropdown padding + icon spacing padding
    const calculatedWidth = `calc(${maxChar}ch + 80px)`;

    return (
      <Select
        size="small"
        value={currentDropdownFilter}
        onChange={(e) => {
          setDropdownFilter(e.target.value as string);
          setPage(1);
        }}
        sx={{
          minWidth: 150,
          borderRadius: "15px",
          height: "40px",
          p: { md: "24px" },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.mode === "dark" ? theme.palette.border.default : theme.palette.border.default,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.mode === "dark" ? theme.palette.border.default : theme.palette.border.default,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.mode === "dark" ? theme.palette.border.default : theme.palette.border.default,
          },
          color: "inherit",
          fontSize: "14px",
          textTransform: "capitalize",
          width: { xs: "100%", md: calculatedWidth }
        }}
        MenuProps={{
          disableScrollLock: true,
          PaperProps: {
            style: { maxHeight: "none" },
            sx: {
              bgcolor: "back.table",
              border: "1px solid",
              borderColor: "border.default",
              borderRadius: "15px",
              boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.4)",
              mt: 1,
            }
          },
          MenuListProps: {
            sx: {
              p: 1,
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              maxHeight: "unset",
            }
          }
        }}
      >
        {!removeAll && (
          <MenuItem
            value="all"
            sx={{
              borderRadius: "10px",
              textTransform: "capitalize",
              fontSize: "14px",
              p: "8px 12px",
              minHeight: "auto",
              "&.Mui-selected": {
                backgroundColor: "#f5f5f5",
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  opacity: 0.9,
                }
              },
              "&:hover": {
                backgroundColor: "border.default",
              }
            }}
          >
            {mainFilter ? mainFilter : "All"}
          </MenuItem>
        )}
        {dropdownFilter.values.map((value, index) => {
          const optValue = typeof value === "string" ? value : (value[1] as any).join(",");
          const optLabel = typeof value === "string" ? value : value[0];
          return (
            <MenuItem
              key={index}
              value={optValue}
              sx={{
                borderRadius: "10px",
                textTransform: "capitalize",
                fontSize: "14px",
                p: "8px 12px",
                minHeight: "auto",
                "&.Mui-selected": {
                  backgroundColor: "#f5f5f5",
                  color: "text.primary",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    opacity: 0.9,
                  }
                },
                "&:hover": {
                  backgroundColor: "border.default",
                }
              }}
            >
              {optLabel}
            </MenuItem>
          );
        })}
      </Select>
    );
  };

  return (
    <TableWrap sx={{ paddingX: { xs: "18px", md: "24px" }, backgroundColor: "back.table", borderRadius: "20px" }}>
      <TableTitle
        sx={{
          display: hideTitle ? "none" : "flex",
          paddingY: { xs: "18px", md: sizing(6).md },
          paddingBottom: sizing(6),
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
        }}
        ref={titleRef}
      >
        <Text medium style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 16 }}>
          {isMobile ? mobileTitle : title}

          {filter && (
            <>
              <FilterBlock
                sx={{
                  "@media (min-width: 600px)": {
                    display: "flex",
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                  },
                  "@media (max-width: 599px)": {
                    flexDirection: "row",
                  },
                }}
              >
                {!removeAll && (
                  <ActionBtn
                    disabled={currentFilter === "all"}
                    variant={currentFilter === "all" ? "contained" : "outlined"}
                    size="medium"
                    disableRipple={true}
                    onClick={() => setFilter("all")}
                    sx={{
                      "@media (max-width: 600px)": {
                        size: "small",
                        margin: 0.5,
                      },
                    }}
                  >
                    {mainFilter ? mainFilter : "All"}
                  </ActionBtn>
                )}

                {filter?.values.map((value, index) => (
                  <ActionBtn
                    key={index}
                    onClick={() => {
                      setFilter(typeof value === "string" ? value : value[1].join(","));
                      setPage(1);
                    }}
                    disabled={currentFilter === value || currentFilter === (value[1] as any).join(",")}
                    variant={
                      currentFilter === value || currentFilter === (value[1] as any).join(",")
                        ? "contained"
                        : "outlined"
                    }
                    size="medium"
                    sx={{
                      "@media (max-width: 600px)": {
                        size: "small",
                        margin: 0.5,
                      },
                    }}
                  >
                    {typeof value === "string" ? value : value[0]}
                  </ActionBtn>
                ))}
              </FilterBlock>

              {/* <Box sx={{
                display: 'none',
                '@media (max-width: 599px)': {
                  display: 'block'
                }
              }}>
                <IconButton onClick={handleClick}>
                  <FilterListIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                >

                  <MenuItem onClick={() => {
                    setFilter("all")

                    handleClose();
                  }}>
                    All
                  </MenuItem>

                  {filter?.values.map((value) => (
                    <MenuItem onClick={() => {
                      setFilter(typeof value === "string" ? value : value[1].join(","))

                      handleClose();
                    }}>

                       {typeof value === "string" ? value.toUpperCase() : value[0].toUpperCase()}
                    </MenuItem>
                  ))}
                </Menu>
              </Box> */}
            </>
          )}

          {!filter && renderDropdownFilter()}
        </Text>

        {((filter && dropdownFilter) || (!(paginationDisabled || totalItems === 0) && totalPages > 1)) && (
          <Box sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            alignItems: isMobile ? 'flex-start' : 'center',
            width: isMobile ? '100%' : 'auto',
            justifyContent: 'flex-end',
            py: isMobile ? 1 : 0
          }}>
            {filter && renderDropdownFilter()}

            {!(paginationDisabled || totalItems === 0) && totalPages > 1 && (
              <PaginationBlock
                sx={{
                  display: paginationDisabled || totalItems === 0 ? "none" : "flex",
                  overflowX: "auto",
                  visibility: totalPages === 1 ? "hidden" : "visible",
                  flexDirection: isMobile ? "column" : "row",
                  justifyContent: "space-between",
                  width: "100%",
                  py: 2,
                }}
              >
                {isMobile ? (
                  <>
                    <Button
                      onClick={() => handlePageClick(1)}
                      disabled={page === 1 || loading}
                      size="medium"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                        px: 2,
                        width: "80px",
                        height: "40px",
                        fontSize: "14px",
                      }}
                    >
                      <KeyboardDoubleArrowLeftIcon />
                    </Button>
                    <Button
                      onClick={() => handlePageClick(page - 1)}
                      disabled={page === 1 || loading}
                      sx={{
                        minWidth: "40px",
                        height: "40px",
                        p: 1,
                        borderRadius: "50%",
                      }}
                    >
                      <ChevronLeftIcon />
                    </Button>

                    <Container
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "border.default",
                        borderRadius: 2,
                        px: 2,
                        width: "80px",
                        height: "40px",
                        fontSize: "14px",
                      }}
                    >
                      {`${page}/${totalPages}`}
                    </Container>
                    <Button
                      onClick={() => handlePageClick(page + 1)}
                      disabled={page === totalPages || loading}
                      sx={{
                        minWidth: "40px",
                        height: "40px",
                        p: 1,
                        borderRadius: "50%",
                      }}
                    >
                      <ChevronRightIcon />
                    </Button>
                    <Button
                      onClick={() => handlePageClick(totalPages)}
                      size="medium"
                      disabled={page === totalPages || loading}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                        px: 2,
                        width: "80px",
                        height: "40px",
                        fontSize: "14px",
                      }}
                    // style={{ border: page === totalPages || loading ? "none" : "1px solid #000", padding: "5px 10px" }}
                    >
                      <KeyboardDoubleArrowRightIcon />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button onClick={() => handlePageClick(1)} disabled={page === 1 || loading} size="medium">
                      <KeyboardDoubleArrowLeftIcon />
                    </Button>
                    <Button
                      onClick={() => handlePageClick(page - 1)}
                      disabled={page === 1 || loading}
                      style={{ padding: "5px 15px" }}
                    >
                      {"<"}
                    </Button>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        border: "1px ",
                        borderRadius: "15px",
                        padding: "5px 15px",
                        backgroundColor: "border.default",
                        fontSize: "14px",
                      }}
                    >
                      {"Page " + page + " of " + totalPages}
                    </Box>
                    <Button
                      onClick={() => handlePageClick(page + 1)}
                      disabled={page === totalPages || loading}
                      style={{ padding: "5px 15px" }}
                    >
                      {">"}
                    </Button>
                    <Button
                      onClick={() => handlePageClick(totalPages)}
                      size="medium"
                      disabled={page === totalPages || loading}
                    >
                      <KeyboardDoubleArrowRightIcon />
                    </Button>
                  </>
                )}
              </PaginationBlock>
            )}
          </Box>
        )}
      </TableTitle>
      <TableContent ref={scrollRef}>
        <LayerStack>
          {/* Full skeleton only during initial load (not pagination), in its own layer under the
              table so the reveal can cross-fade the two in place. The two tables size their columns
              independently, so the visible header lives with whichever one holds the rows: the
              skeleton's while loading, the table's from the moment the reveal starts. */}
          {skeletonMounted && (
            <SkeletonLayer aria-hidden className={showSkeleton ? undefined : "is-revealed"}>
              <Table>
                <TableHeader schema={schema} equalColumnWidths={equalColumnWidths} hidden={!showSkeleton} />
                {getPreloaderSkeleton()}
              </Table>
            </SkeletonLayer>
          )}

          <Table>
            <TableHeader schema={schema} equalColumnWidths={equalColumnWidths} hidden={showSkeleton} />

            {!currentLoading && error && (
              <NoData
                className={revealClass}
                sx={{ paddingY: sizing(6), height: { xs: 38, md: 64 }, color: "error.main" }}
              >
                Failed to load {errorName} data...
              </NoData>
            )}

            {/* Show data with column-level skeletons during pagination, or normal data when not loading */}
            {((isPaginating && data.length > 0) || (!currentLoading && !error)) && (
              <>
                {customData ? (
                  customData.length > 0 ? (
                    <TableBody className={revealClass}>{getDataMapper(getPaginatedCustomData())}</TableBody>
                  ) : !currentLoading ? (
                    <NoData className={revealClass} sx={{ paddingY: sizing(6), height: { xs: 38, md: 64 } }}>
                      No {errorName} data found...
                    </NoData>
                  ) : null
                ) : data.length > 0 ? (
                  <TableBody className={revealClass}>{getDataMapper(data)}</TableBody>
                ) : !currentLoading ? (
                  <NoData className={revealClass} sx={{ paddingY: sizing(6), height: { xs: 38, md: 64 } }}>
                    No {errorName} data
                  </NoData>
                ) : null}
              </>
            )}
          </Table>
        </LayerStack>
      </TableContent>

      <TableTitle sx={{ paddingBottom: { xs: "18px", md: sizing(6).md }, borderBottom: "none", paddingTop: sizing(6) }}>
        {link ? <TableLink to={link + `?currentFilter=${currentFilter}`}>Show more...</TableLink> : <div />}
        <PaginationBlock
          sx={{
            display: paginationDisabled || totalItems === 0 || totalItems < 1 ? "none" : "flex",
            overflowX: "auto",
            visibility: totalPages === 1 ? "hidden" : "visible",
            flexDirection: isMobile ? "row" : "row",
            justifyContent: "space-between",
            // gap: 1,
            width: "100%",
            py: 2,
          }}
        >
          {isMobile ? (
            <>
              <Button
                onClick={() => handlePageClick(1)}
                disabled={page === 1 || loading}
                size="medium"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 2,
                  px: 2,
                  width: "80px",
                  height: "40px",
                  fontSize: "14px",
                }}
              >
                <KeyboardDoubleArrowLeftIcon />
              </Button>
              <Button
                onClick={() => handlePageClick(page - 1)}
                disabled={page === 1 || loading}
                sx={{
                  minWidth: "40px",
                  height: "40px",
                  p: 1,
                  borderRadius: "50%",
                }}
              >
                <ChevronLeftIcon />
              </Button>

              <Container
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "border.default",
                  borderRadius: 2,
                  px: 2,
                  width: "80px",
                  height: "40px",
                  fontSize: "14px",
                }}
              >
                {`${page}/${totalPages}`}
              </Container>
              <Button
                onClick={() => handlePageClick(page + 1)}
                disabled={page === totalPages || loading}
                sx={{
                  minWidth: "40px",
                  height: "40px",
                  p: 1,
                  borderRadius: "50%",
                }}
              >
                <ChevronRightIcon />
              </Button>
              <Button
                onClick={() => handlePageClick(totalPages)}
                size="medium"
                disabled={page === totalPages || loading}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 2,
                  px: 2,
                  width: "80px",
                  height: "40px",
                  fontSize: "14px",
                }}
              // style={{ border: page === totalPages || loading ? "none" : "1px solid #000", padding: "5px 10px" }}
              >
                <KeyboardDoubleArrowRightIcon />
              </Button>
            </>
          ) : (
            // Original pagination for desktop
            <>
              <Button
                onClick={() => handlePageClick(1)}
                disabled={page === 1 || loading}
                size="medium"
              // style={{ border: page === 1 || loading ? "none" : "1px solid #000", padding: "5px 10px" }}
              >
                <KeyboardDoubleArrowLeftIcon />
              </Button>
              <Button
                onClick={() => handlePageClick(page - 1)}
                disabled={page === 1 || loading}
                style={{ padding: "5px 15px" }}
              >
                {"<"}
              </Button>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  border: "1px ",
                  borderRadius: "15px",
                  padding: "5px 15px",
                  backgroundColor: "border.default",
                  fontSize: "14px",
                }}
              >
                {"Page " + page + " of " + totalPages}
              </Box>
              {/* <ActionBtn
            sx={{ width: "auto", padding: "5px 10px" }}
            disabled={true}
            variant={"contained"}
            size="medium"
          >{"Page " + page + " of " + totalPages}</ActionBtn> */}
              <Button
                onClick={() => handlePageClick(page + 1)}
                disabled={page === totalPages || loading}
                style={{ padding: "5px 15px" }}
              >
                {">"}
              </Button>
              <Button
                onClick={() => handlePageClick(totalPages)}
                size="medium"
                disabled={page === totalPages || loading}
              // style={{ border: page === totalPages || loading ? "none" : "1px solid #000", padding: "5px 10px" }}
              >
                <KeyboardDoubleArrowRightIcon />
              </Button>
            </>
          )}
        </PaginationBlock>
      </TableTitle>
    </TableWrap>
  );
};

const TableHeader = ({
  schema,
  equalColumnWidths,
  hidden,
}: {
  schema: TableSchema<any>[];
  equalColumnWidths?: boolean;
  hidden?: boolean;
}) => {
  const columnWidth = equalColumnWidths ? `${100 / schema.length}%` : "auto";

  return (
    <HeaderRow style={hidden ? { visibility: "hidden" } : undefined}>
      {schema.map((column, i) => {
        return (
          <Cell
            key={i}
            sx={{
              whiteSpace: "nowrap",
              textTransform: { xs: "capitalize", md: "uppercase" },
              pt: sizing(6),
              pb: sizing(6),
              paddingLeft: 0,
              paddingRight: { xs: "18px", md: "24px" },
              width: equalColumnWidths ? columnWidth : "auto",
              ...column.sx,
            }}
          >
            <Text numeric contrast>
              {column.label}
            </Text>
          </Cell>
        );
      })}
    </HeaderRow>
  );
};

const TableWrap = styled("div")`
  width: 100%;
  height: 100%;
`;

const TableContent = styled("div")`
  width: 100%;
  overflow-x: scroll;

  /* Drag-to-scroll affordance. Both attributes are set by useDragScroll straight on the node:
     data-draggable only while the content actually overflows, data-dragging only mid-drag.
     Gated to real pointers — a touch screen pans natively and has no cursor to change. */
  @media (hover: hover) and (pointer: fine) {
    &[data-draggable] {
      cursor: grab;
    }

    &[data-dragging] {
      cursor: grabbing;
      user-select: none;
    }
  }
`;

const Table = styled("div")`
  display: table;
  width: 100%;
`;

// The skeleton layer and the table share one grid cell, so the reveal swaps them in place with no
// layout jump. align-self: start stops the grid stretching a table to the taller layer's height —
// a stretched table hands the extra height to its rows, which would balloon the header.
const LayerStack = styled("div")`
  display: grid;

  & > * {
    grid-area: 1 / 1;
    align-self: start;
  }
`;

const SkeletonLayer = styled("div")`
  pointer-events: none;
  opacity: 1;

  /* Pulse the bars, not the layer, so the layer's opacity / filter stay free for the reveal.
     Replaces MUI's own 2s pulse so every table's skeleton keeps the same beat. */
  & .MuiSkeleton-root {
    animation: ${skeletonPulse} ${PULSE_MS}ms ease-in-out infinite;
  }

  &.is-revealed {
    opacity: 0;
    transition: opacity ${REVEAL_MS}ms ${REVEAL_EASE};
  }

  @media (prefers-reduced-motion: reduce) {
    & .MuiSkeleton-root {
      animation: none;
    }

    &.is-revealed {
      transition: none;
    }
  }
`;

// Rows arriving from the skeleton fade in, one layer per row rather than per cell. One-shot: the
// class drops once the reveal is over.
const TableBody = styled("div")`
  display: table-row-group;

  &.is-revealing > div {
    animation: ${contentReveal} ${REVEAL_MS}ms ${REVEAL_EASE} both;
  }

  @media (prefers-reduced-motion: reduce) {
    &.is-revealing > div {
      animation: none;
    }
  }
`;

const TableTitle = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  & > span:first-of-type {
    line-height: 1;
  }

  // border-bottom: 1px solid #3d3d3d;
  width: 100%;
`;

const HeaderRow = styled("div")`
  display: table-header-group;
  & > div {
    border-bottom: 1px solid #3d3d3d;
    border-top: 1px solid #3d3d3d;
    line-height: 1;
    font-family: IBM Mono, monospace;
    display: table-cell;
  }
`;

const TableRow = styled("div")`
  display: table-row;
  & > div {
    font-family: IBM Mono, monospace;
    border-bottom: 1px solid #3d3d3d;
    display: table-cell;
  }
`;

export const FilterBlock = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  overflow-x: auto;
  white-space: nowrap;

  @media (max-width: 600px) {
    flex-direction: row;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding-bottom: 8px;
  }
  & button {
    border: 1px solid
      ${({ theme }) => (theme.palette.mode === "dark" ? theme.palette.border.default : theme.palette.border.default)};
    border-radius: 15px;
    text-transform: capitalize;
    padding: 6px 16px;
    transition: none;
    box-sizing: border-box;

    @media (max-width: 600px) {
      padding: 4px 12px;
      font-size: 0.875rem;
    }

    &:hover {
      animation: none;
      transform: none;
      transition: none;
      border: 1px solid
        ${({ theme }) => (theme.palette.mode === "dark" ? theme.palette.border.default : theme.palette.border.default)};
      width: auto;
      height: auto;
      box-sizing: border-box;
    }

    &.active {
      border: 1px solid transparent;
    }

    &:disabled {
      background-color: ${({ theme }) => theme.palette.border.default};
      color: ${({ theme }) => theme.palette.text.primary};
      pointer-events: none;
    }
  }
`;

// removed text - decoration: underline; from pagination block
const PaginationBlock = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 12px;
  max-width: fit-content;
  @media (max-width: 600px) {
    flex-direction: row;
    align-items: center;
    gap: 10px;
  }

  button {
    width: 24px;
    border-radius: 15px;
    text-transform: capitalize;
    line-height: 1;
    cursor: pointer;
    font-size: 14px;

    &:hover {
      border: 1px solid
        ${({ theme }) => (theme.palette.mode === "dark" ? theme.palette.border.default : theme.palette.border.default)};
    }

    @media (max-width: 992px) {
      font-size: 14px;
    }

    background-color: transparent;
    border: 1px solid
      ${({ theme }) => (theme.palette.mode === "dark" ? theme.palette.border.default : theme.palette.border.default)};
    color: inherit;

    // &:first-of-type,
    // &:last-of-type {
    //   text-decoration: none;
    // }

    &:disabled {
      cursor: not-allowed;
      color: ${({ theme }) => theme.palette.text.primary};
      background-color: #c3c3c3;
      border: 1px solid
        ${({ theme }) => (theme.palette.mode === "dark" ? theme.palette.border.default : theme.palette.border.default)};
      opacity: 0.6;
    }
  }
`;

const Cell = styled("div")`
  vertical-align: top;
  font-size: 16px;
  @media (max-width: 992px) {
    font-size: 14px;
  }
  & > div,
  & > span {
    white-space: nowrap;
  }

  & .userAddress,
  & .trustAddress {
    text-decoration: underline;
  }
`;

const NoData = styled("div")`
  color: ${({ theme }) => theme.palette.text.disabled};
  width: 100%;
  display: table-caption;
  caption-side: bottom;
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.dark};

  &.is-revealing {
    animation: ${contentReveal} ${REVEAL_MS}ms ${REVEAL_EASE} both;
  }

  @media (prefers-reduced-motion: reduce) {
    &.is-revealing {
      animation: none;
    }
  }
`;

export default TableComponent;